import React from 'react';
import { Helmet } from 'react-helmet';
import JsonLdList from '../../../common/seo/JsonLdList';
import { SITE_URL, getBreadcrumbSchema, organizationSchema } from '../../../../utils/seo';
import { buildFaqSchema, buildHowToSchema, buildWebPageSchema } from '../../../../utils/schema';
import pageAssets from '../../../../data/pageAssets';

// JPG/PNG rather than WebP: social crawlers handle WebP inconsistently.
const FAN_SHARE_IMAGE = `${SITE_URL}${pageAssets.fanClassic}`;

const FansSeo = ({ seo, faqItems, structuredProducts, buyingGuide, catalogRows }) => {
    const schemas = [
        organizationSchema,
        getBreadcrumbSchema([
            { name: 'Home', url: `${SITE_URL}/` },
            { name: 'Ceiling Fans', url: `${SITE_URL}/fans` }
        ]),
        {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": "NISHAD Ceiling Fan Collection",
            "description": seo.productGroupDescription,
            "url": `${SITE_URL}/fans`,
            "about": [
                { "@type": "Thing", "name": "Ceiling fan", "sameAs": "https://www.wikidata.org/wiki/Q1181944" }
            ],
            "speakable": {
                "@type": "SpeakableSpecification",
                "cssSelector": [".amazer-section", ".fan-section-heading"]
            }
        },
        ...(catalogRows && catalogRows.length > 0 ? [{
            "@context": "https://schema.org",
            "@type": "ItemList",
            "name": "NISHAD Ceiling Fan Models",
            "itemListElement": catalogRows.map((fan, index) => ({
                "@type": "ListItem",
                "position": index + 1,
                "name": `${fan.name} Ceiling Fan`,
                "url": `${SITE_URL}/fans`
            }))
        }] : []),
        buildWebPageSchema({
            name: seo.webPageName,
            url: `${SITE_URL}/fans`,
            description: seo.webPageDescription,
            about: [
                { "@type": "Thing", "name": "Ceiling fan", "sameAs": "https://www.wikidata.org/wiki/Q1181944" },
                { "@type": "Thing", "name": "1200mm Ceiling Fans" }
            ],
            speakable: ['.amazer-section', '.faq-section']
        }),
        {
            "@context": "https://schema.org",
            "@type": "ProductGroup",
            "name": seo.productGroupName,
            "url": `${SITE_URL}/fans`,
            "description": seo.productGroupDescription,
            "brand": {
                "@type": "Brand",
                "name": "NISHAD"
            },
            "category": "Ceiling Fans",
            "productGroupID": "nishad-ceiling-fans",
            "variesBy": ["finish", "model"],
            "hasVariant": structuredProducts.map((fan) => ({
                "@type": "Product",
                "name": `NISHAD ${fan.name} Ceiling Fan`,
                "description": `${fan.name} ceiling fan in ${fan.finish} finish with ${fan.features.slice(0, 3).join(', ')}.`,
                "image": `${SITE_URL}${pageAssets[fan.imageKey]}`,
                "brand": {
                    "@type": "Brand",
                    "name": "NISHAD"
                },
                "additionalProperty": fan.features.map((feature) => ({
                    "@type": "PropertyValue",
                    "name": "Feature",
                    "value": feature
                }))
            }))
        },
        buildFaqSchema(faqItems),
        ...(buyingGuide ? [buildHowToSchema({
            name: buyingGuide.title,
            description: buyingGuide.description,
            steps: buyingGuide.items
        })] : [])
    ];

    return (
        <>
            <Helmet>
                <title>{seo.title}</title>
                <meta name="description" content={seo.description} />
                <meta name="keywords" content={seo.keywords} />
                <meta name="robots" content="index, follow" />
                <meta name="author" content="Nishad Cables" />
                <link rel="canonical" href={`${SITE_URL}/fans`} />

                <meta name="geo.region" content="IN-RJ" />
                <meta name="geo.placename" content="Alwar" />
                <meta name="geo.position" content="27.5530; 76.6346" />
                <meta name="ICBM" content="27.5530, 76.6346" />

                <meta property="og:type" content="website" />
                <meta property="og:title" content={seo.title} />
                <meta property="og:description" content={seo.ogDescription} />
                <meta property="og:image" content={FAN_SHARE_IMAGE} />
                <meta property="og:image:alt" content="NISHAD high speed ceiling fans" />
                <meta property="og:url" content={`${SITE_URL}/fans`} />
                <meta property="og:site_name" content="NISHAD" />
                <meta property="og:locale" content="en_IN" />

                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={seo.title} />
                <meta name="twitter:description" content={seo.twitterDescription} />
                <meta name="twitter:image" content={FAN_SHARE_IMAGE} />
            </Helmet>
            <JsonLdList items={schemas} />
        </>
    );
};

export default FansSeo;
